import React from 'react';
import { connect } from 'react-redux';
import { compose, withHandlers, withStateHandlers } from 'recompose';
import Autosuggest from 'react-autosuggest';
import classnames from 'classnames';
import { DeleteOutlined } from '@ant-design/icons';
import { Input } from '@mm/components';
import { ENTER_KEY_NAME, TAB_KEY_NAME } from '@mm/constants/keys-codes';
import './VariableItem.pcss';

const renderSuggestion = (suggestion, { isHighlighted }) => (
  <div
    className={classnames('webhook-variable__suggestions-item', {
      'webhook-variable__suggestions-item--selected': isHighlighted,
    })}
  >
    <div className="webhook-variable__suggestion-name">
      <span>{suggestion.name}</span>
    </div>
  </div>
);

const VariableItem = props => {
  const {
    sentAs,
    filteredVariables,
    onRemoveLocal,
    onBlur,
    value,
    onSuggestionsFetchRequested,
    onSuggestionSelected,
    renderInput,
    doesValueExist,
    setFilteredVariables,
    onValueChange,
    isStatic,
    onChangeSentAs,
    onChangeStaticValue,
    staticValue,
  } = props;
  const inputProps = {
    placeholder: 'Value',
    value,
    onChange: onValueChange,
    autoComplete: 'off',
  };

  return (
    <div className="webhook-variable__container">
      <div className="webhook-variable__sent-as">
        <Input
          value={sentAs}
          onBlur={onBlur}
          onChange={onChangeSentAs}
          placeholder="Name"
        />
      </div>
      <div className="webhook-variable__name">
        {isStatic ? (
          <Input
            value={staticValue}
            onBlur={onBlur}
            placeholder="Static Value"
            onChange={onChangeStaticValue}
          />
        ) : (
          <Autosuggest
            suggestions={filteredVariables.filter(
              variable => !doesValueExist(variable),
            )}
            shouldRenderSuggestions={() => true}
            onSuggestionSelected={onSuggestionSelected}
            onSuggestionsFetchRequested={onSuggestionsFetchRequested}
            onSuggestionsClearRequested={() => setFilteredVariables([])}
            getSuggestionValue={suggestion => suggestion}
            renderInputComponent={renderInput}
            renderSuggestion={renderSuggestion}
            inputProps={inputProps}
          />
        )}
      </div>
      <DeleteOutlined
        className="webhook-variable__btn"
        onClick={onRemoveLocal}
      />
    </div>
  );
};

const onChangeName =
  ({ setName }) =>
  e => {
    setName(e.target.value);
  };
const onChangeSentAs =
  ({ setSentAs }) =>
  e => {
    setSentAs(e.target.value);
  };
const onValueChange =
  ({ setValue }) =>
  e => {
    if (typeof e.target.value === 'string') {
      setValue(e.target.value);
    }
  };
const onChangeStaticValue =
  ({ setStaticValue }) =>
  e => {
    setStaticValue(e.target.value);
  };

const renderInput =
  ({ filteredVariables, connectionVariables, onSuggestionSelected }) =>
  inputProps => {
    const onKeyDown = e => {
      const { value } = e.target;
      if (e.key === TAB_KEY_NAME || e.key === ENTER_KEY_NAME) {
        e.preventDefault();
        if (value) {
          const variable = filteredVariables.find(
            item => item.name.toLowerCase() === value.toLowerCase(),
          );
          const isVariableOnConnection =
            variable &&
            connectionVariables.findIndex(
              item => item.name.toLowerCase() === value.toLowerCase(),
            ) > -1;
          if (variable && !isVariableOnConnection) {
            onSuggestionSelected(e, { suggestionValue: variable });
          }
        }
      }
      inputProps.onKeyDown(e);
    };
    return <Input {...inputProps} onKeyDown={onKeyDown} />;
  };

const onSuggestionSelected =
  ({
    index,
    setName,
    sentAs,
    setSentAs,
    setValue,
    connectionVariables,
    onVariableUpdate,
  }) =>
  async (e, { suggestionValue }) => {
    const { name: suggestionName } = suggestionValue;
    const sentAsOrSuggestionName = sentAs.length > 0 ? sentAs : suggestionName;
    const variable = {
      ...connectionVariables[index],
      sentAs: sentAsOrSuggestionName,
      name: suggestionName,
    };

    setSentAs(sentAsOrSuggestionName);
    setName(suggestionName);
    setValue(suggestionName);

    onVariableUpdate(variable, index);
  };

const shouldUpdateVariable = props => updatedVariable => {
  const { isStatic, staticValue, name, sentAs } = props;
  if (isStatic) {
    const isValEq = updatedVariable.value
      ? updatedVariable.value === staticValue
      : !!staticValue;
    const isDiff = !(updatedVariable.sentAs === sentAs && isValEq);
    return staticValue && sentAs && isDiff;
  }

  const isDiff = !(
    updatedVariable.name === name && updatedVariable.sentAs === sentAs
  );
  return name && sentAs && isDiff;
};

const onBlur = props => async () => {
  const {
    name,
    sentAs,
    index,
    onVariableUpdate,
    connectionVariables,
    isStatic,
    staticValue,
    shouldUpdateVariable,
  } = props;
  const variable = connectionVariables[index];

  if (!shouldUpdateVariable(variable)) {
    return;
  }

  const updatedVariable = isStatic
    ? { ...variable, name: sentAs, sentAs, value: staticValue, static: true }
    : { ...variable, name, sentAs, static: false };
  onVariableUpdate(updatedVariable, index);
};

const onSuggestionsFetchRequested =
  ({ variables, setFilteredVariables }) =>
  ({ value }) => {
    const filteredVariables = variables.filter(
      item => item.name.toLowerCase().indexOf(value.toLowerCase()) !== -1,
    );

    setFilteredVariables(filteredVariables);
  };

const doesValueExist =
  ({ connectionVariables, index }) =>
  (value, checkWithIndex = false) => {
    const variableIndex = connectionVariables.findIndex(
      variable => variable.name === value.name,
    );
    if (checkWithIndex) {
      return variableIndex > -1 && variableIndex === index;
    }
    return variableIndex > -1;
  };

const onRemoveLocal =
  ({ onRemove }) =>
  async index => {
    onRemove(index);
  };

const initialState = ({ variable }) => ({
  name: variable.name,
  value: variable.name,
  staticValue: variable.value,
  sentAs: variable.sentAs,
  isStatic: variable.static,
  filteredVariables: [],
});

const stateUpdaters = {
  setName: () => name => ({ name }),
  setSentAs: () => sentAs => ({ sentAs }),
  setFilteredVariables: () => filteredVariables => ({ filteredVariables }),
  setValue: () => value => ({ value }),
  setStaticValue: () => staticValue => ({ staticValue }),
};

const mapStateToProps = state => ({
  variables: state.variables.entities,
});

const enhance = compose(
  connect(mapStateToProps, null),
  withStateHandlers(initialState, stateUpdaters),
  withHandlers({ shouldUpdateVariable }),
  withHandlers({
    renderInput,
    onChangeName,
    onValueChange,
    onChangeSentAs,
    onSuggestionsFetchRequested,
    onSuggestionSelected,
    doesValueExist,
    onChangeStaticValue,
    onRemoveLocal,
    onBlur,
  }),
);

export default enhance(VariableItem);
